/**
 * @file WmsReceiptsListPage.tsx
 * @description Реестр приёмок (phase13.0; фильтры — phase19.23, A6;
 * phase19.31 зона «Склад» — переведён целиком на Commora).
 *
 * Заголовок «Приёмки» в верхней панели, главное действие одно — «+ Приёмка»
 * (на телефоне и планшете — в общей нижней полосе каркаса, а не плавающая
 * самодельная кнопка). Строка фильтров: поиск, статус пилюлями, склад. Список
 * — `ResponsiveList` (таблица от 1024, компактный список до 1024).
 *
 * Поиск, статус, склад и страницы считает сервер (`useWmsDocumentsList`):
 * журнал растёт, и клиентский фильтр по последней сотне врал бы «ничего не
 * нашлось». `?status=DRAFT&wh=…` — вход с плитки «Черновики» обзора склада.
 */

import { ArrowDownToLine, Plus, Search } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";

import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterBar } from "../../../components/ui/FilterBar";
import { FilterChips } from "../../../components/ui/FilterChips";
import { SearchInput } from "../../../components/ui/SearchInput";
import { SelectField } from "../../../components/ui/SelectField";
import { RECEIPT_COLUMNS } from "../../../components/wms/registry/receipt-columns";
import { WmsDocumentsListBody } from "../../../components/wms/registry/WmsDocumentsListBody";
import { useWmsDocumentsList } from "../../../components/wms/useWmsDocumentsList";
import { warehouseOptions } from "../../../lib/wms-registry-filters";

/** Статусы приёмки — пилюлями; новый статус = запись. */
const STATUS_CHIPS: { value: "" | "DRAFT" | "COMMITTED"; label: string }[] = [
  { value: "", label: "Все" },
  { value: "DRAFT", label: "Черновики" },
  { value: "COMMITTED", label: "Проведённые" },
];

/** Известные статусы — всё лишнее из адреса игнорируем (`?status=`). */
function parseStatusParam(raw: string | null): string {
  return STATUS_CHIPS.some((chip) => chip.value && chip.value === raw) ? (raw ?? "") : "";
}

export function WmsReceiptsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const basePath = `/shop/${slug}/wms/receipts`;
  const list = useWmsDocumentsList("receipts", "Не удалось загрузить приёмки. Проверьте связь и попробуйте ещё раз.", {
    status: parseStatusParam(searchParams.get("status")),
    warehouseId: searchParams.get("wh") ?? "",
  });
  const createLink = (
    <Button asChild variant="brand">
      <Link to={`${basePath}/new`}>
        <Plus className="size-4" />
        Приёмка
      </Link>
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Приёмки" subtitle="Поступление товара на склад" action={createLink} />

      <FilterBar
        search={
          <SearchInput
            value={list.search}
            onChange={list.setSearch}
            placeholder="Номер, поставщик или накладная"
            ariaLabel="Поиск по приёмкам"
          />
        }
        // Телефон: статус — в шторке «Фильтры · N» (единое решение склада).
        chipsCollapse={{ activeCount: list.status ? 1 : 0, onReset: () => list.setStatus("") }}
        chips={
          <FilterChips
            aria-label="Статус приёмки"
            items={STATUS_CHIPS}
            value={list.status as (typeof STATUS_CHIPS)[number]["value"]}
            onChange={list.setStatus}
          />
        }
      >
        <SelectField
          value={list.warehouseId}
          onChange={list.setWarehouseId}
          options={warehouseOptions(list.warehouses)}
          placeholder="Все склады"
          ariaLabel="Склад"
          className="w-full sm:w-[220px]"
        />
      </FilterBar>

      <WmsDocumentsListBody
        list={list}
        columns={RECEIPT_COLUMNS}
        rowKey={(r) => r.id}
        rowHref={(r) => `${basePath}/${r.id}`}
        rowLabel={(r) => `Приёмка ${r.receiptNumber}`}
        ariaLabel="Реестр приёмок"
        filtered={list.filtering}
        empty={{
          icon: ArrowDownToLine,
          title: "Приёмок пока нет",
          description: "Создайте первую — товары встанут на склад после проведения.",
        }}
        filteredEmpty={{
          icon: Search,
          title: "Под эти условия приёмок нет",
          description: "Измените поиск, статус или склад.",
          onReset: list.reset,
        }}
        allShownText="Показаны все приёмки"
      />
    </div>
  );
}
